import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * FilterPanel Props
 */
interface FilterPanelProps {
  /** Whether panel is open */
  isOpen: boolean;
  /** Panel title */
  title?: string;
  /** Callback when panel should close */
  onClose: () => void;
  /** Callback when "Apply" button clicked */
  onApply: () => void;
  /** Callback when "Reset" button clicked */
  onReset: () => void;
  /** Optional ref for initial focus */
  initialFocusRef?: React.RefObject<HTMLElement>;
  /** Filter controls content */
  children: React.ReactNode;
}

/**
 * FilterPanel Component
 * Right-side fly-out panel with portal rendering, focus trap, and keyboard support
 *
 * Spec: Per Carrot_Filtering_System_Specification.md §8 Progressive Enhancement
 *
 * Accessibility:
 * - Portal rendering to document.body
 * - role="dialog" with aria-modal="true"
 * - Focus trap when open
 * - Esc key to close
 * - Focus returns to opener on close
 * - Scroll lock on body when open
 */
export function FilterPanel({
  isOpen,
  title = 'Filters',
  onClose,
  onApply,
  onReset,
  initialFocusRef,
  children,
}: FilterPanelProps) {
  const panelRef = useRef<HTMLElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Handle Esc key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Focus management and scroll lock
  useEffect(() => {
    if (isOpen) {
      // Save current focus
      previousFocusRef.current = document.activeElement as HTMLElement;

      // Lock body scroll
      document.body.setAttribute('data-lock', 'scroll');
      document.body.style.overflow = 'hidden';

      // Focus panel or initial element
      setTimeout(() => {
        if (initialFocusRef?.current) {
          initialFocusRef.current.focus();
        } else if (panelRef.current) {
          panelRef.current.focus();
        }
      }, 100);
    } else {
      // Restore scroll
      document.body.removeAttribute('data-lock');
      document.body.style.overflow = '';

      // Restore focus to opener
      if (previousFocusRef.current) {
        previousFocusRef.current.focus();
      }
    }

    return () => {
      document.body.removeAttribute('data-lock');
      document.body.style.overflow = '';
    };
  }, [isOpen, initialFocusRef]);

  // Focus trap
  useEffect(() => {
    if (!isOpen || !panelRef.current) return;

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      const focusableElements = panelRef.current!.querySelectorAll<HTMLElement>(
        'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const focusableArray = Array.from(focusableElements);

      if (focusableArray.length === 0) return;

      const firstElement = focusableArray[0];
      const lastElement = focusableArray[focusableArray.length - 1];

      if (e.shiftKey) {
        // Shift+Tab: if on first, go to last
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab: if on last, go to first
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTab);
    return () => document.removeEventListener('keydown', handleTab);
  }, [isOpen]);

  // Backdrop click handler
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const panelContent = (
    <div
      className={`c-filter-panel-root${isOpen ? ' is-open' : ''}`}
      aria-hidden={!isOpen}
    >
      {/* Backdrop */}
      <div
        className="c-filter-panel-backdrop"
        onClick={handleBackdropClick}
        aria-label="Close filters panel"
      />

      {/* Panel */}
      <aside
        ref={panelRef}
        className="c-filter-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="filter-panel-title"
        tabIndex={-1}
      >
        {/* Header */}
        <header className="c-filter-panel-head">
          <h2 id="filter-panel-title">{title}</h2>
          <button
            type="button"
            className="c-filter-close"
            onClick={onClose}
            aria-label="Close filters panel"
          >
            <span aria-hidden="true">×</span>
          </button>
        </header>

        {/* Body - filter controls */}
        <div className="c-filter-panel-body">{children}</div>

        {/* Footer */}
        <footer className="c-filter-panel-foot">
          <button
            type="button"
            className="c-filter-reset"
            onClick={onReset}
          >
            Reset
          </button>
          <button
            type="button"
            className="c-filter-apply"
            onClick={onApply}
          >
            Apply
          </button>
        </footer>
      </aside>
    </div>
  );

  return createPortal(panelContent, document.body);
}
